/**
 * Asset Type Validator
 *
 * Comprehensive validation and mapping utilities to ensure all asset types
 * are properly handled across the application.
 */

import { AssetKind } from "@/components/assets/AddAssetModal";
import { AssetClass } from "@/types/financial";

// All supported asset types from backend
export const BACKEND_ASSET_TYPES = [
  "gold",
  "pf",
  "ppf",
  "vpf",
  "insurance",
  "stocks",
  "mutual_funds",
  "bonds",
  "real_estate",
  "crypto",
  "commodities",
  "fixed_deposits",
  "bank_balance",
  "other",
] as const;

// All frontend asset kinds
export const FRONTEND_ASSET_KINDS: AssetKind[] = [
  "gold",
  "insurance",
  "pf",
  "ppf",
  "vpf",
  "realEstate",
  "crypto",
  "bankBalance",
  "fixedDeposits",
  "stocks",
  "mutualFunds",
  "bonds",
  "commodities",
];

// All asset classes from types
export const ASSET_CLASSES: AssetClass[] = [
  "Mutual Funds",
  "Shares",
  "Smallcase",
  "PF",
  "PPF",
  "VPF",
  "Insurance",
  "Fixed Deposits",
  "Savings",
  "Gold",
  "Real Estate",
  "Bank Balance",
  "Crypto",
  "Stocks",
  "Others",
];

/**
 * Comprehensive mapping from backend asset_type to frontend AssetKind
 */
export const BACKEND_TO_FRONTEND_MAPPING: Record<string, AssetKind> = {
  gold: "gold",
  pf: "pf",
  ppf: "ppf",
  vpf: "vpf",
  insurance: "insurance",
  stocks: "stocks",
  mutual_funds: "mutualFunds",
  bonds: "bonds",
  real_estate: "realEstate",
  crypto: "crypto",
  commodities: "commodities",
  fixed_deposits: "fixedDeposits",
  bank_balance: "bankBalance",
  other: "gold", // Default fallback
};

/**
 * Reverse mapping from frontend AssetKind to backend asset_type
 */
export const FRONTEND_TO_BACKEND_MAPPING: Record<AssetKind, string> = {
  gold: "gold",
  pf: "pf",
  ppf: "ppf",
  vpf: "vpf",
  insurance: "insurance",
  stocks: "stocks",
  mutualFunds: "mutual_funds",
  bonds: "bonds",
  realEstate: "real_estate",
  crypto: "crypto",
  commodities: "commodities",
  fixedDeposits: "fixed_deposits",
  bankBalance: "bank_balance",
};

/**
 * Mapping from AssetKind to display category names
 */
export const ASSET_KIND_TO_DISPLAY_NAME: Record<AssetKind, string> = {
  gold: "Gold",
  pf: "PF",
  ppf: "PPF",
  vpf: "VPF",
  insurance: "Insurance",
  stocks: "Stocks",
  mutualFunds: "Mutual Funds",
  bonds: "Bonds",
  realEstate: "Real Estate",
  crypto: "Crypto",
  commodities: "Commodities",
  fixedDeposits: "Fixed Deposits",
  bankBalance: "Bank Balance",
};

/**
 * All possible category name variations for matching
 */
export const CATEGORY_VARIATIONS: Record<AssetKind, string[]> = {
  gold: ["gold"],
  pf: ["pf", "provident fund"],
  ppf: ["ppf", "public provident fund"],
  vpf: ["vpf", "voluntary provident fund"],
  insurance: ["insurance"],
  stocks: ["stocks", "stock"],
  mutualFunds: ["mutual funds", "mutualfunds", "mutual fund", "mutual_funds"],
  bonds: ["bonds", "bond"],
  realEstate: ["real estate", "realestate", "real_estate"],
  crypto: ["crypto", "cryptocurrency"],
  commodities: ["commodities", "commodity"],
  fixedDeposits: ["fixed deposits", "fixeddeposits", "fixed deposit", "fixed_deposits"],
  bankBalance: ["bank balance", "bankbalance", "bank", "bank_balance"],
};

/**
 * Validate that all backend asset types have frontend mappings
 */
export function validateAssetTypeMappings(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check that all backend types have frontend mappings
  for (const backendType of BACKEND_ASSET_TYPES) {
    if (!BACKEND_TO_FRONTEND_MAPPING[backendType]) {
      errors.push(`Backend asset type '${backendType}' has no frontend mapping`);
    }
  }

  // Check that all frontend kinds have backend mappings
  for (const frontendKind of FRONTEND_ASSET_KINDS) {
    if (!FRONTEND_TO_BACKEND_MAPPING[frontendKind]) {
      errors.push(`Frontend asset kind '${frontendKind}' has no backend mapping`);
    }
  }

  // Check that all frontend kinds have display names
  for (const frontendKind of FRONTEND_ASSET_KINDS) {
    if (!ASSET_KIND_TO_DISPLAY_NAME[frontendKind]) {
      errors.push(`Frontend asset kind '${frontendKind}' has no display name`);
    }
  }

  // Check that all frontend kinds have category variations
  for (const frontendKind of FRONTEND_ASSET_KINDS) {
    if (!CATEGORY_VARIATIONS[frontendKind]) {
      errors.push(`Frontend asset kind '${frontendKind}' has no category variations`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get all asset types that should be supported
 */
export function getAllSupportedAssetTypes(): {
  backend: readonly string[];
  frontend: AssetKind[];
  displayNames: string[];
} {
  return {
    backend: BACKEND_ASSET_TYPES,
    frontend: FRONTEND_ASSET_KINDS,
    displayNames: FRONTEND_ASSET_KINDS.map((kind) => ASSET_KIND_TO_DISPLAY_NAME[kind]),
  };
}

/**
 * Check if an asset type is supported
 */
export function isAssetTypeSupported(assetType: string): boolean {
  return (
    (BACKEND_ASSET_TYPES as readonly string[]).includes(assetType) ||
    (FRONTEND_ASSET_KINDS as readonly string[]).includes(assetType)
  );
}

/**
 * Normalize any asset type string to a consistent format
 */
export function normalizeAssetType(assetType: string): AssetKind | null {
  if (!assetType) return null;

  const lowerType = assetType.toLowerCase().trim();

  // Direct mapping
  if (BACKEND_TO_FRONTEND_MAPPING[lowerType]) {
    return BACKEND_TO_FRONTEND_MAPPING[lowerType];
  }

  // Check category variations
  for (const [kind, variations] of Object.entries(CATEGORY_VARIATIONS)) {
    if (variations.some((variation) => lowerType === variation.toLowerCase())) {
      return kind as AssetKind;
    }
  }

  return null;
}

/**
 * Get comprehensive asset type information
 */
export function getAssetTypeInfo(assetType: string): {
  backendType: string;
  frontendKind: AssetKind | null;
  displayName: string | null;
  isSupported: boolean;
} {
  const frontendKind = normalizeAssetType(assetType);
  const backendType = frontendKind ? FRONTEND_TO_BACKEND_MAPPING[frontendKind] : assetType;
  const displayName = frontendKind ? ASSET_KIND_TO_DISPLAY_NAME[frontendKind] : null;

  return {
    backendType,
    frontendKind,
    displayName,
    // If we can normalize the type, we treat it as supported (includes display-name variants).
    isSupported: frontendKind !== null,
  };
}
